import { deepEqual } from "fast-equals";
import { classes } from "polytype";
import slugify from "slugify";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import type { Project } from "../../project/index.js";
import { provideReact, type AsyncResourceVariant } from "../../react/index.js";
import type { ContainerEnvVariableList } from "./ContainerEnvVariable.js";
import type { ContainerPortList } from "./ContainerPort.js";
import { ContainerState } from "./ContainerState.js";
import { ImageMeta } from "./ImageMeta.js";
import type {
  ContainerData,
  ContainerListItemData,
  ContainerListQueryData,
  ContainerPatchRequestData,
  ContainerStatus,
  ContainerUpdateData,
} from "./types.js";

export class Container extends ReferenceModel {
  public readonly stackId: string;

  public constructor(id: string, stackId: string) {
    super(id);
    this.stackId = stackId;
  }

  public static ofId(id: string, stackId: string) {
    return new Container(id, stackId);
  }

  public static find = provideReact(async (id: string, stackId: string) => {
    const data = await config.behaviors.container.find(id, stackId);
    if (data !== undefined) {
      return new ContainerDetailed(data);
    }
  });

  public static get = provideReact(async (id: string, stackId: string) => {
    const container = await this.find(id, stackId);
    assertObjectFound(container, "container", id);
    return container;
  });

  public findDetailed = provideReact(
    () => Container.find(this.id, this.stackId),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ContainerDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Container.get(this.id, this.stackId),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ContainerDetailed>>;

  public static query(project: Project, query: ContainerListQueryData = {}) {
    return new ContainerListQuery(project, query);
  }

  public static async create(
    projectId: string,
    data: ContainerPatchRequestData,
  ) {
    // todo: handling for creating new volumes at the same time
    const response = await config.behaviors.container.create(projectId, data);

    if ("id" in response) {
      return new Container(response.id, projectId);
    }

    return response;
  }

  public async start() {
    await config.behaviors.container.start(this.id, this.stackId);
  }

  public async stop() {
    await config.behaviors.container.stop(this.id, this.stackId);
  }

  public async restart() {
    await config.behaviors.container.restart(this.id, this.stackId);
  }

  public async recreate() {
    await config.behaviors.container.recreate(this.id, this.stackId);
  }

  public static generateServiceName(description: string) {
    return slugify(description, {
      lower: true,
      strict: true,
    });
  }
}

class ContainerCommon extends classes(
  DataModel<ContainerListItemData | ContainerData>,
  Container,
) {
  public readonly shortId: string;
  public readonly description: string;
  public readonly serviceName: string;
  public readonly deployedState: ContainerState;
  public readonly pendingState: ContainerState;
  public readonly entrypoint?: string;
  public readonly command?: string;
  public readonly status?: ContainerStatus;
  public readonly message?: string;
  public readonly recreateRequired?: boolean;

  public constructor(data: ContainerListItemData | ContainerData) {
    super([data], [data.id, data.stackId]);
    this.shortId = data.shortId;
    this.description = data.description;
    this.serviceName = data.serviceName;
    this.deployedState = new ContainerState(data.deployedState);
    this.pendingState = new ContainerState(data.pendingState);
    this.status = data.status;
    this.message = data.message;
    this.entrypoint = data.pendingState.entrypoint?.join(" ");
    this.command = data.pendingState.command?.join(" ");
    this.recreateRequired = !deepEqual(data.pendingState, data.deployedState);
  }

  public async delete() {
    await config.behaviors.container.delete(this.serviceName, this.stackId);
  }

  public async update(data: ContainerUpdateData) {
    await config.behaviors.container.update(this.stackId, {
      services: {
        [this.serviceName]: {
          description:
            this.description.trim() !== data.description?.trim()
              ? data.description
              : undefined,
          image:
            this.pendingState.image.trim() !== data.image?.trim()
              ? data.image
              : undefined,
        },
      },
    });
  }

  public async updateEntrypoint(
    entrypoint: string,
    custom?: boolean,
  ): Promise<ContainerDetailed | void> {
    const result = await config.behaviors.container.update(this.stackId, {
      services: {
        [this.serviceName]: {
          entrypoint:
            entrypoint.trim() !== this.entrypoint?.trim() && custom
              ? entrypoint.split(" ")
              : [],
        },
      },
    });

    const containerData = result.services?.find(
      (s) => s.serviceName === this.serviceName,
    );

    return containerData ? new ContainerDetailed(containerData) : undefined;
  }

  public async updateCommand(
    command: string,
    custom?: boolean,
  ): Promise<ContainerDetailed | void> {
    const result = await config.behaviors.container.update(this.stackId, {
      services: {
        [this.serviceName]: {
          command:
            command.trim() !== this.command?.trim() && custom
              ? command.split(" ")
              : [],
        },
      },
    });

    const containerData = result.services?.find(
      (s) => s.serviceName === this.serviceName,
    );

    return containerData ? new ContainerDetailed(containerData) : undefined;
  }

  public async updateVolumes(): Promise<ContainerDetailed | void> {
    /* todo: implement */
  }

  public async updatePorts(
    ports: ContainerPortList,
  ): Promise<ContainerDetailed | void> {
    const result = await config.behaviors.container.update(this.stackId, {
      services: {
        [this.serviceName]: {
          ports: ports.toStringArray(),
        },
      },
    });

    const containerData = result.services?.find(
      (s) => s.serviceName === this.serviceName,
    );

    return containerData ? new ContainerDetailed(containerData) : undefined;
  }

  public async updateEnvs(
    envs: ContainerEnvVariableList,
  ): Promise<ContainerDetailed | void> {
    const result = await config.behaviors.container.update(this.stackId, {
      services: {
        [this.serviceName]: {
          envs: envs.toApiDataObject(),
        },
      },
    });

    const containerData = result.services?.find(
      (s) => s.serviceName === this.serviceName,
    );

    return containerData ? new ContainerDetailed(containerData) : undefined;
  }

  public async resetToDeployedState() {
    await config.behaviors.container.update(this.stackId, {
      services: {
        [this.serviceName]: {
          image: this.deployedState.image,
          envs: this.deployedState.envs
            ? this.deployedState.envs.toApiDataObject()
            : {},
          ports: this.deployedState.ports.toStringArray(),
          volumes: this.deployedState.volumes.toStringArray(),
        },
      },
    });
  }

  public async downloadLog() {
    const response = await config.behaviors.container.getLog(
      this.id,
      this.stackId,
    );

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," + encodeURIComponent(response),
    );
    element.setAttribute("target", "_blank");
    element.setAttribute("download", `${this.serviceName}.log`);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }

  public getLog = provideReact(async () => {
    return config.behaviors.container.getLog(this.id, this.stackId);
  }, [this.id, this.stackId]);

  public static getImageMeta = provideReact(
    async (imageRef: string, projectId: string, generateAiData?: boolean) => {
      const result = await config.behaviors.container.getImageMeta(
        imageRef,
        projectId,
        generateAiData,
      );

      if ("isAiAvailable" in result) {
        return new ImageMeta(result, imageRef);
      }
      return result;
    },
  );
}

export class ContainerDetailed extends classes(
  ContainerCommon,
  DataModel<ContainerData>,
) {
  public constructor(data: ContainerData) {
    super([data], [data]);
  }
}

export class ContainerListItem extends classes(
  ContainerCommon,
  DataModel<ContainerListItemData>,
) {
  public constructor(data: ContainerListItemData) {
    super([data], [data]);
  }
}

export class ContainerListQuery extends ListQueryModel<ContainerListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: ContainerListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: ContainerListQueryData) {
    return new ContainerListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.container.list(
      this.project.id,
      this.query,
    );

    return new ContainerList(
      this.project,
      this.query,
      items.map((c) => new ContainerListItem(c)),
    );
  }, [this.queryId, "containers"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "containers"]);
}

export class ContainerList extends classes(
  ContainerListQuery,
  ListDataModel<ContainerListItem>,
) {
  public constructor(
    project: Project,
    query: ContainerListQueryData,
    containers: ContainerListItem[],
  ) {
    super([project, query], [containers, containers.length]);
  }
}
